import { useAuth } from '@/auth/Auth'
import { usePermissions } from '@/auth/PermissionsContext'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/hooks/use-toast'
import { cn, userInitials } from '@/lib/utils'
import { ArrowLeftRight, Check, Loader2, LogOut, Store, UserCircle, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface UserDropdownProps {
  profilePhotoUrl?: string
}

export function UserDropdown({ profilePhotoUrl }: UserDropdownProps) {
  const { authData, switchStore } = useAuth()
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [switchingStoreId, setSwitchingStoreId] = useState<string | null>(null)

  const { hasPermission, isPrimary } = usePermissions()

  const stores = authData?.stores ?? []
  const currentStore = stores.find((s) => s.is_current)
  const primaryStore = stores.find((s) => s.is_primary)
  const primarySwitchBlocked = Boolean(primaryStore?.login_blocked && !primaryStore?.is_current)
  const isPrimaryAccount = currentStore?.is_primary ?? false
  const isCollaborator = !isPrimaryAccount
  const isOnboardingFinished = authData?.onboarding_step === 'finished'

  const accountName = authData?.account?.name ?? authData?.name
  const accountEmail = authData?.account?.email ?? authData?.email
  const accountInitials = userInitials(accountName ?? '')

  const currentStoreName = currentStore?.store?.name

  const canAccessMyAccount =
    isPrimary ||
    hasPermission('account.view_profile_identity') ||
    hasPermission('account.view_address') ||
    hasPermission('account.view_notification_settings')

  const canAccessTeam = (isPrimary || hasPermission('team.manage_team')) && isOnboardingFinished

  const handleSwitchStore = async (membershipUuid: string) => {
    const isCurrent = stores.find((s) => s.membership_uuid === membershipUuid)?.is_current
    if (!membershipUuid || isCurrent) return
    setSwitchingStoreId(membershipUuid)
    try {
      await switchStore(membershipUuid)
      toast({ description: t('store_switched') })
    } catch {
      toast({ description: t('store_switch_error'), variant: 'error' })
    } finally {
      setSwitchingStoreId(null)
    }
  }

  const handleGoToPrimaryAccount = async () => {
    if (!primaryStore) return

    if (primaryStore.is_current) {
      navigate('/my-account')
      return
    }

    if (primaryStore.login_blocked) {
      toast({ description: t('store_switch_error'), variant: 'error' })
      return
    }

    setSwitchingStoreId(primaryStore.membership_uuid)
    try {
      await switchStore(primaryStore.membership_uuid)
      toast({ description: t('store_switched') })
      navigate('/my-account')
    } catch {
      toast({ description: t('store_switch_error'), variant: 'error' })
    } finally {
      setSwitchingStoreId(null)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="outline-none">
        <div className="flex items-center gap-2">
          {isCollaborator && (
            <div className="hidden sm:flex items-center gap-1.5 rounded-full bg-[#a9d342]/10 border border-[#a9d342]/20 px-2.5 py-1">
              <ArrowLeftRight className="size-3 text-[#a9d342]" />
              <span className="text-[11px] font-medium text-[#a9d342] leading-none max-w-[120px] truncate">
                {currentStoreName}
              </span>
            </div>
          )}
          <div className="relative">
            <Avatar className="size-8 cursor-pointer select-none ring-2 ring-transparent transition-all hover:opacity-90 hover:ring-colorBorder/40 data-[state=open]:ring-primary/30">
              <AvatarImage src={profilePhotoUrl} alt={t('profile_photo')} className="object-cover" />
              <AvatarFallback className="bg-elevated text-sm font-medium text-colorText">
                {accountInitials}
              </AvatarFallback>
            </Avatar>
            {isCollaborator && (
              <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-[#a9d342] border-2 border-black" />
            )}
          </div>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-[calc(100vw-2rem)] sm:w-80 rounded-xl p-1.5"
        align="end"
        sideOffset={8}
      >
        {isCollaborator && (
          <div className="mx-1 mb-1.5 rounded-lg bg-[#a9d342]/10 border border-[#a9d342]/20 px-3 py-2.5">
            <div className="flex items-start gap-2.5">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#a9d342]/20">
                <ArrowLeftRight className="size-3.5 text-[#a9d342]" />
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-[#a9d342]/70 leading-none">
                  {t('viewing_as_collaborator')}
                </p>
                <p className="text-sm font-semibold text-[#a9d342] leading-tight truncate">
                  {currentStoreName}
                </p>
              </div>
            </div>
          </div>
        )}

        <DropdownMenuItem
          disabled={primarySwitchBlocked}
          className={cn(
            'rounded-lg py-3 gap-3 focus:bg-accent/50',
            primarySwitchBlocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
          onClick={handleGoToPrimaryAccount}
        >
          <Avatar className="size-10 ring-1 ring-colorBorder/20">
            <AvatarImage src={profilePhotoUrl} className="object-cover" />
            <AvatarFallback className="bg-elevated text-sm font-semibold text-colorText">
              {accountInitials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5 min-w-0">
            <p className="truncate font-semibold text-sm leading-none">{accountName}</p>
            <p className="truncate text-muted-foreground text-xs leading-none">{accountEmail}</p>
          </div>
        </DropdownMenuItem>

        {(canAccessMyAccount || canAccessTeam) && (
          <>
            <div className="mx-1 my-1.5 h-px bg-colorBorder/50" />
            <DropdownMenuGroup>
              {canAccessMyAccount && (
                <DropdownMenuItem
                  className="cursor-pointer rounded-lg gap-2.5 py-2"
                  onClick={() => navigate('/my-account')}
                >
                  <UserCircle className="size-4 text-muted-foreground" />
                  <span className="text-sm">{t('my_account')}</span>
                </DropdownMenuItem>
              )}
              {canAccessTeam && (
                <DropdownMenuItem
                  className="cursor-pointer rounded-lg gap-2.5 py-2"
                  onClick={() => navigate('/team')}
                >
                  <Users className="size-4 text-muted-foreground" />
                  <span className="text-sm">{t('team')}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </>
        )}

        {authData?.auth_subject === 'account' && (
          <>
            <div className="mx-1 my-1.5 h-px bg-colorBorder/50" />
            <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/50">
              {t('switch_store')}
            </DropdownMenuLabel>
            {stores
              .filter((entry) => !entry.login_blocked)
              .map((entry) => {
              const isCurrent = entry.is_current
              const isSwitching = switchingStoreId === entry.membership_uuid

              return (
                <DropdownMenuItem
                  key={entry.membership_uuid}
                  className={cn(
                    'rounded-lg gap-2.5 py-2.5 relative overflow-hidden',
                    isCurrent
                      ? 'bg-primary/[0.06] cursor-default pointer-events-none'
                      : 'cursor-pointer',
                  )}
                  onClick={(e) => {
                    if (isCurrent || isSwitching) {
                      e.preventDefault()
                      e.stopPropagation()
                      return
                    }
                    handleSwitchStore(entry.membership_uuid)
                  }}
                >
                  {isCurrent && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5/6 rounded-full bg-primary" />
                  )}
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback
                      className={cn(
                        'rounded-lg text-xs font-semibold',
                        isCurrent
                          ? 'bg-primary/20 text-primary'
                          : 'bg-elevated text-muted-foreground',
                      )}
                    >
                      <Store className="size-3.5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span
                      className={cn(
                        'truncate text-sm font-medium leading-none',
                        isCurrent && 'text-primary',
                      )}
                    >
                      {entry.store.name}
                    </span>
                    <span className="text-muted-foreground text-[11px] leading-none">
                      {entry.read_only
                        ? t('read_only', { defaultValue: 'Somente leitura' })
                        : entry.is_primary
                          ? t('owner')
                          : t('collaborator')}
                    </span>
                  </div>
                  {isSwitching ? (
                    <Loader2 className="size-4 animate-spin text-primary flex-shrink-0" />
                  ) : isCurrent ? (
                    <Check className="size-4 text-primary flex-shrink-0" />
                  ) : null}
                </DropdownMenuItem>
              )
            })}
          </>
        )}

        <div className="mx-1 my-1.5 h-px bg-colorBorder/50" />

        <DropdownMenuItem
          className="cursor-pointer rounded-lg gap-2.5 py-2 text-muted-foreground focus:text-destructive focus:bg-destructive/5"
          onClick={() => navigate('/logout')}
        >
          <LogOut className="size-4" />
          <span className="text-sm">{t('signout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
